import { CAMPAIGN_OUTCOME_STATUSES, type CampaignOutcomeRow } from './campaign.schema';
import { OUTCOME_STATUS_META } from './outcome-status-meta';

/**
 * Campaign outcome table (SCRUM-73) + inline status tagging (SCRUM-74).
 *
 * Columns match the shipped API (`CampaignOutcomeRow`): Customer · Product · Match ·
 * Referred By · Outcome · Date. ("Assigned To" from the mockup is intentionally omitted — the
 * outcome model has no owner field yet; tracked as SCRUM-785.) Referred-By reuses the ledger's
 * brand-gold amber for teller attribution, bound to the server-resolved `referredByName` (NOT
 * the client referrals-store — plan G2). Outcome is an editable dropdown (SCRUM-74) offering
 * all five statuses; the server is the transition authority (no client-side flow gating — D4).
 *
 * Styling is entirely Ryan's design tokens (SCRUM-6); the table scrolls horizontally within its
 * own container so it never forces page overflow at narrow widths (.claude/rules/frontend.md responsive rule).
 */

const COLUMNS = [
  { key: 'customer', label: 'CUSTOMER', align: 'text-left' },
  { key: 'product', label: 'PRODUCT', align: 'text-left' },
  { key: 'match', label: 'MATCH', align: 'text-left' },
  { key: 'referredBy', label: 'REFERRED BY', align: 'text-left' },
  { key: 'outcome', label: 'OUTCOME', align: 'text-left' },
  { key: 'date', label: 'DATE', align: 'text-right' },
] as const;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function CampaignOutcomeTable({
  outcomes,
  onStatusChange,
  pendingCustomerId,
}: {
  outcomes: readonly CampaignOutcomeRow[];
  onStatusChange: (customerId: string, status: CampaignOutcomeRow['status']) => void;
  /** Customer id whose status PATCH is in flight (disables that row's control). */
  pendingCustomerId?: string | null;
}) {
  return (
    <div className="border-border-1 bg-bg-3 rounded-tk-md overflow-x-auto border">
      <table className="w-full border-collapse text-[0.8125rem]">
        <thead>
          <tr className="border-border-1 border-b">
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={`text-text-3 px-3 py-2.5 font-mono text-[0.6875rem] font-medium tracking-[0.06em] whitespace-nowrap uppercase ${col.align}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {outcomes.map((row) => (
            <OutcomeTableRow
              key={row.customerId}
              row={row}
              onStatusChange={onStatusChange}
              pending={pendingCustomerId === row.customerId}
            />
          ))}
          {outcomes.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length} className="text-text-3 px-3 py-8 text-center">
                No customers in this campaign.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function OutcomeTableRow({
  row,
  onStatusChange,
  pending,
}: {
  row: CampaignOutcomeRow;
  onStatusChange: (customerId: string, status: CampaignOutcomeRow['status']) => void;
  pending: boolean;
}) {
  const meta = OUTCOME_STATUS_META[row.status];
  return (
    <tr className="border-border-1 hover:bg-bg-4 border-b last:border-b-0">
      <td className="text-text-0 px-3 py-2.5 font-medium whitespace-nowrap">{row.customerName}</td>
      <td className="text-text-1 px-3 py-2.5 whitespace-nowrap">{row.productName ?? '—'}</td>
      <td className="text-text-1 px-3 py-2.5 whitespace-nowrap">
        <span className="text-text-0 font-medium">{row.matchMet}</span>
        <span className="text-text-3"> /{row.matchTotal}</span>
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        {row.referredByName ? (
          <span className="text-brand-primary font-medium">{row.referredByName}</span>
        ) : (
          <span className="text-text-3">—</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {/* SCRUM-74: editable outcome. The select is tinted with the current status's token
            pair so it reads as a badge while remaining a native, accessible control. */}
        <select
          aria-label={`Outcome for ${row.customerName}`}
          value={row.status}
          disabled={pending}
          onChange={(e) => {
            onStatusChange(row.customerId, e.target.value as CampaignOutcomeRow['status']);
          }}
          className={`rounded-tk-full focus:border-brand-primary cursor-pointer border border-transparent py-[3px] pr-6 pl-2.5 font-mono text-[0.6875rem] font-medium outline-none disabled:cursor-wait disabled:opacity-60 ${meta.text} ${meta.bg}`}
        >
          {CAMPAIGN_OUTCOME_STATUSES.map((status) => (
            <option key={status} value={status} className="bg-bg-3 text-text-0 font-body">
              {OUTCOME_STATUS_META[status].label}
            </option>
          ))}
        </select>
      </td>
      <td className="text-text-1 px-3 py-2.5 text-right whitespace-nowrap">
        {formatDate(row.updatedAt)}
      </td>
    </tr>
  );
}
